import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, CircleAlert, X } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import RuvixxDate from "components/RuvixxDate";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { formatError } from "helpers/formatError";
import MetaAdAccountService from "services/MetaAdAccount";
import { MetaAccountsTableActions } from "routes/meta_leads/MetaAccountsTableActions";

const ACCOUNTS_QUERY_KEY = ["meta-ad-accounts"];

const buildColumns = ({ canManage, onEdit, onDelete }) => [
  {
    header: "ID",
    id: "ID",
    accessorKey: "id",
    size: 50,
    cell: ({ row }) => (
      <p style={{ fontWeight: "bold" }}>{row.getValue("ID")}</p>
    ),
  },
  { id: "Name", accessorKey: "name", header: "Name", size: 180 },
  {
    id: "Ad Account ID",
    accessorKey: "ad_account_id",
    header: "Ad Account ID",
    size: 180,
  },
  { id: "Page ID", accessorKey: "page_id", header: "Page ID", size: 160 },
  {
    id: "Syncing",
    accessorKey: "is_sync_enabled",
    header: "Syncing",
    size: 90,
    enableSorting: false,
    cell: ({ row }) =>
      row.getValue("Syncing") ? (
        <Check
          className="tw:size-4 tw:text-[#24B388]"
          aria-label="Syncing enabled"
        />
      ) : (
        <X
          className="tw:size-4 tw:text-muted-foreground"
          aria-label="Syncing disabled"
        />
      ),
  },
  {
    id: "Last Sync",
    // The consolidated live status across all domains, matching the detail
    // rail; the account's own column tracks only the leads sync.
    accessorFn: row => row.sync?.live?.status,
    header: "Last Sync",
    size: 120,
    enableSorting: false,
  },
  {
    id: "Last Synced",
    // Newest successful sync across all live domains, matching the detail rail.
    accessorFn: row => row.sync?.live?.last_synced_at,
    header: "Last Synced",
    size: 180,
    enableSorting: false,
    cell: ({ row }) => {
      const date = row.getValue("Last Synced");
      return date ? <RuvixxDate date={date} /> : null;
    },
  },
  {
    header: null,
    id: "Actions",
    accessorKey: "actions",
    size: 60,
    enableSorting: false,
    enableResizing: false,
    meta: { isDisplay: true },
    cell: ({ row }) => (
      <MetaAccountsTableActions
        account={row.original}
        canManage={canManage}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    ),
  },
];

const ManageAccountsPage = () => {
  const navigate = useNavigate();
  const canManage = useAuthorized([
    ACL_RELATIONSHIPS.metaAdAccount.manageAccounts,
  ]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  usePageTitle("Meta accounts");

  const invalidateAccounts = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY }),
    []
  );

  const handleEdit = useCallback(
    account => navigate(`/settings/integrations/meta/${account.id}/edit`),
    [navigate]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        canManage,
        onEdit: handleEdit,
        onDelete: setDeleteTarget,
      }),
    [canManage, handleEdit]
  );

  const fetchAccounts = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return MetaAdAccountService.list(next);
    },
    [columns]
  );

  const handleDelete = useCallback(async () => {
    try {
      await MetaAdAccountService.delete(deleteTarget.id);
      toast.success("Account deleted");
      setDeleteTarget(null);
      invalidateAccounts();
    } catch (error) {
      toast.error(formatError(error, "Could not delete account"));
    }
  }, [deleteTarget, invalidateAccounts]);

  if (!canManage) {
    return (
      <div className="tw:w-full">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert />
            </EmptyMedia>
            <EmptyTitle>Access denied</EmptyTitle>
            <EmptyDescription>
              You don&apos;t have permission to manage Meta accounts.
            </EmptyDescription>
          </EmptyHeader>
          <Link to="/">Back to home</Link>
        </Empty>
      </div>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:min-w-0">
      <BaseTableV3
        columns={columns}
        header="Meta accounts"
        tableName="meta_ad_accounts"
        queryKey={ACCOUNTS_QUERY_KEY}
        fetchData={fetchAccounts}
        invalidateData={invalidateAccounts}
        enableSearch
        enableSettings
        createButton={
          <Button
            size="sm"
            onClick={() => navigate("/settings/integrations/meta/new")}
          >
            Connect account
          </Button>
        }
      />
      <Dialog
        open={!!deleteTarget}
        onOpenChange={open => !open && setDeleteTarget(null)}
      >
        <DialogContent className="tw:sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              Delete {deleteTarget?.name}? Its synced leads stop refreshing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAccountsPage;
